import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { useTheme } from '../../theme/ThemeContext'
import { AgentActivityBar, type AgentActivityState } from './AgentActivityBar'
import { agentToolLabel } from './agentDisplay'
import { AgentMarkdown } from './AgentMarkdown'
import { AgentProcessFold, type AgentProcessLine } from './AgentProcessFold'
import { AgentStatusDotSlot } from './AgentStatusDot'
import { AgentSubagentFold, type AgentSubagentBlock } from './AgentSubagentFold'
import { AgentTodoFold, type AgentTodoItem } from './AgentTodoFold'
import { AgentImageStrip } from './AgentImageStrip'
import { AgentGenerationCard, type AgentGenerationCardData } from './AgentGenerationCard'
import type { AgentTimelineImage } from '../../lib/agentTimelineMapper'
import { useTypewriter } from './useTypewriter'

export type AgentTimelineBlock =
  | {
      id: string
      type: 'answer'
      text: string
    }
  | {
      id: string
      type: 'process'
      summary: string
      status?: string
      collapsedByDefault?: boolean
      forceOpen?: boolean
      /** 运行计时起点（毫秒）：思考中等运行中的块标题实时显示「用时 n 秒」 */
      startedAt?: number
      lines: AgentProcessLine[]
      /** 连续同类节点的合并计数（如「工具调用 ×3」） */
      count?: number
      /** 合并分组标记：相同标记且连续到达的块会并入前一块 */
      mergeKey?: string
    }
  | {
      id: string
      type: 'todos'
      items: AgentTodoItem[]
      collapsedByDefault?: boolean
      forceOpen?: boolean
    }
  | ({
      id: string
      type: 'subagent'
    } & Omit<AgentSubagentBlock, 'id'>)
  | {
      id: string
      type: 'images'
      toolName: string
      images: AgentTimelineImage[]
    }
  | {
      id: string
      type: 'generation-card'
      data: AgentGenerationCardData
    }

export type AgentConfirmInline = {
  toolName: string
  summary: string
  confirming?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/** @deprecated 兼容旧命名；新代码用 AgentTimelineBlock */
export type AgentRunSection = AgentTimelineBlock

export type AgentRunCardProps = {
  role: 'assistant' | 'user' | 'system'
  /** 用户气泡正文；助手主回答已拆进 timeline */
  text?: string
  status?: string
  /** 如：模型 · 模式 */
  meta?: string
  timeline?: AgentTimelineBlock[]
  /** 底部实时活动：思考 / 规划下一步 / 等待子智能体等 */
  activity?: AgentActivityState | null
  confirmInline?: AgentConfirmInline | null
  /**
   * 模型静默思考指示：运行中但流上超过数秒无新事件时置真，
   * 在时间线最底部渲染「模型思考中…」占位，新内容到达后自然被覆盖。
  /** 模型静默思考指示：运行中但流上超过数秒无新事件时置真，
   * 在时间线最底部渲染「模型思考中…」占位，新内容到达后自然被覆盖。
   */
  thinking?: boolean
  /** 生图任务卡片状态变更回调（SSE 推送 / 重试后由卡片发起） */
  onGenerationCardChange?: (blockId: string, patch: Partial<AgentGenerationCardData>) => void
  /** 外部持久化的已展开 block id 集合（跨页面恢复展开态） */
  openBlockIds?: Set<string>
  /** 折叠块手动展开/收起回调 */
  onBlockToggle?: (blockId: string, open: boolean) => void
}

export function AgentRunCard({
  role,
  text = '',
  status,
  meta,
  timeline = [],
  activity,
  confirmInline,
  thinking = false,
  onGenerationCardChange,
  openBlockIds,
  onBlockToggle,
}: AgentRunCardProps) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const isUser = role === 'user'
  const isSystem = role === 'system'
  const running = status === 'running' || status === 'pending'

  if (isUser) {
    return (
      <article
        className={[
          angular ? 'rounded-none' : 'rounded-xl',
          'ml-auto w-fit max-w-[80%] px-3 py-2.5 break-words whitespace-pre-wrap sm:px-3.5',
          'bg-[linear-gradient(180deg,color-mix(in_srgb,var(--accent)_12%,var(--panel)),var(--panel))] shadow-card ring-1 ring-[color-mix(in_srgb,var(--accent)_28%,transparent)]',
        ].join(' ')}
      >
        <AgentMarkdown text={text} />
      </article>
    )
  }

  if (isSystem) {
    return (
      <article
        className={[
          angular ? 'rounded-none' : 'rounded-xl',
          'w-full px-3 py-2.5 sm:px-3.5',
          'bg-[color-mix(in_srgb,var(--surface-muted)_55%,var(--panel)_45%)]',
        ].join(' ')}
      >
        <AgentMarkdown text={text} />
      </article>
    )
  }

  const lastProcessId = [...timeline].reverse().find((item) => item.type === 'process')?.id

  /** 活跃中的 process block 已自行承载状态，底部 ActivityBar 不应重复 */
  const activeProcess = timeline.find(
    (item) =>
      item.type === 'process' &&
      (item.status === 'running' ||
        item.status === 'awaiting_confirmation' ||
        item.status === 'pending'),
  )
  const activityVisible =
    !!activity &&
    (activity.kind === 'done' || activity.kind === 'error' || activity.kind === 'confirm' || !activeProcess)

  /** 按事件到达顺序渲染：思考 → 该轮正文 → 下一轮思考 → 下一轮正文，保持多轮交错时序 */
  const lastAnswerId = [...timeline].reverse().find((item) => item.type === 'answer')?.id

  return (
    <article className="agent-run-card mr-auto w-full max-w-[96%] min-w-0">
      {(meta || status) && (
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="min-w-0 truncate text-[15px] font-semibold leading-5 tracking-tight text-ink">
            {meta || ' '}
          </p>
          <AgentStatusDotSlot status={status} size="md" />
        </div>
      )}

      <AgentActivityBar
        activity={
          activityVisible
            ? activity
            : running
              // 不带 kind：避免「思考中 处理中…」式冗余提示；具体阶段由时间线块标题承载
              ? { label: '处理中…' }
              : status === 'awaiting_confirmation'
                ? { label: '等待你确认下一步动作', kind: 'confirm' }
                : null
        }
        running={running}
      />

      <div className="flex min-w-0 flex-col gap-2">
        {timeline.map((block) => {
          if (block.type === 'answer') {
            return (
              <AgentAnswerSegment
                key={block.id}
                text={block.text}
                animate={running && block.id === lastAnswerId}
              />
            )
          }

          if (block.type === 'todos') {
            return (
              <AgentTodoFold
                key={block.id}
                items={block.items}
                collapsedByDefault={block.collapsedByDefault ?? false}
                forceOpen={block.forceOpen}
                externalOpen={openBlockIds?.has(block.id)}
                onToggle={(open) => onBlockToggle?.(block.id, open)}
              />
            )
          }

          if (block.type === 'images') {
            return <AgentImageStrip key={block.id} toolName={block.toolName} images={block.images} />
          }

          if (block.type === 'generation-card') {
            return (
              <AgentGenerationCard
                key={block.id}
                blockId={block.id}
                data={block.data}
                angular={angular}
                onChange={
                  onGenerationCardChange
                    ? (patch) => onGenerationCardChange(block.id, patch)
                    : undefined
                }
              />
            )
          }

          if (block.type === 'subagent') {
            return (
              <AgentSubagentFold
                key={block.id}
                block={{
                  id: block.id,
                  title: block.title,
                  status: block.status,
                  summary: block.summary,
                  collapsedByDefault: block.collapsedByDefault,
                  forceOpen: block.forceOpen,
                  lines: block.lines,
                  activity: block.activity,
                  externalOpen: openBlockIds?.has(block.id),
                  onToggle: (open) => onBlockToggle?.(block.id, open),
                }}
              />
            )
          }

          const hostConfirm = confirmInline && block.id === lastProcessId ? confirmInline : null
          return (
            <AgentProcessFold
              key={block.id}
              summary={block.summary}
              status={block.status}
              startedAt={block.startedAt}
              collapsedByDefault={block.collapsedByDefault ?? true}
              forceOpen={Boolean(block.forceOpen || hostConfirm)}
              externalOpen={openBlockIds?.has(block.id)}
              onToggle={(open) => onBlockToggle?.(block.id, open)}
              lines={block.lines}
              footer={
                hostConfirm ? (
                  <ConfirmStrip angular={angular} confirm={hostConfirm} />
                ) : null
              }
            />
          )
        })}

        {confirmInline && !lastProcessId ? (
          <ConfirmStrip angular={angular} confirm={confirmInline} />
        ) : null}

        {running && thinking && !activeProcess ? (
          <div
            className="flex items-center gap-2 px-1 py-0.5 text-[12px] text-ink-soft"
            role="status"
            aria-live="polite"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[color-mix(in_srgb,var(--accent)_70%,transparent)] opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
            </span>
            模型思考中…
          </div>
        ) : null}
      </div>
    </article>
  )
}

/** 正文分段：流式运行中的最后一段启用打字机追字，历史分段直接完整展示 */
function AgentAnswerSegment({ text, animate }: { text: string; animate: boolean }) {
  const shown = useTypewriter(text, animate)
  return (
    <div className="agent-answer-segment min-w-0">
      <AgentMarkdown text={shown} />
    </div>
  )
}

function ConfirmStrip({
  angular,
  confirm,
}: {
  angular: boolean
  confirm: AgentConfirmInline
}) {
  return (
    <div
      className={[
        'agent-confirm-strip flex flex-wrap items-center justify-between gap-2 px-2.5 py-2',
        angular
          ? 'rounded-none border border-line bg-surface-muted/70'
          : 'rounded-lg border border-[color-mix(in_srgb,var(--line)_70%,var(--accent)_30%)] bg-surface-muted/70',
      ].join(' ')}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-ink">
          确认「{agentToolLabel(confirm.toolName)}」
        </p>
        <p className="mt-0.5 text-[11px] leading-4 text-ink-soft">{confirm.summary}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <ChromeToolbarButton type="button" disabled={confirm.confirming} onClick={confirm.onCancel}>
          取消
        </ChromeToolbarButton>
        <ChromePrimaryButton
          density="compact"
          disabled={confirm.confirming}
          onClick={confirm.onConfirm}
        >
          {confirm.confirming ? '执行中…' : '确认执行'}
        </ChromePrimaryButton>
      </div>
    </div>
  )
}
